import type { BlockPerimeters } from "building-types/blocks/blockTypes";
import { WallLateralDatumAnchor } from "building-types/datums/datumsTypesEnum";
import type { WallSettings } from "building-types/settings/wallSettings";
import { MULTIGABLE_BSP_EPSILON } from "doc/utils/roofTools";
import { getExteriorWallOffset } from "doc/utils/wallTypeTools";
import { aabb2ContainsVec2, aabb2Grow } from "geom/aabb2";
import { addPolygonsToBSP } from "geom/bsp";
import { addEdgeToPolygonList } from "geom/bspClipping";
import { BSP_EPSILON, type NodeBSP, type PolygonBSP } from "geom/bspTypes";
import { DIST_EPSILON, Line2Side } from "geom/core";
import { joinPolyLine2Rails } from "geom/join";
import { line2Side } from "geom/line2";
import { offsetPolyLine2 } from "geom/offset";
import {
  polyLine2AABB2,
  polyLine2Concat,
  polyLine2Edge,
  polyLine2EdgeCount,
  polyLine2End,
  polyLine2Extend,
  polyLine2Reverse,
  polyLine2Start,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";

interface MakeUpperFloorLimitBSPOptions {
  boundingPerimeter: PolyLine2;
  upperFloorPerimeters: BlockPerimeters;
  edgeOverhangs: number[];
  wallSettings: WallSettings;
  baseHeight: number;
  upperFloorCeilElevation: number;
  heelHeightOffset: number;
}

export function makeUpperFloorLimitBSP({
  boundingPerimeter,
  upperFloorPerimeters,
  edgeOverhangs,
  wallSettings,
  baseHeight,
  upperFloorCeilElevation,
  heelHeightOffset,
}: MakeUpperFloorLimitBSPOptions): NodeBSP | null {
  if (baseHeight !== undefined && baseHeight - heelHeightOffset >= upperFloorCeilElevation - DIST_EPSILON) {
    return null;
  }

  const exteriorWallOffsetWithSheathing = getExteriorWallOffset(
    wallSettings,
    WallLateralDatumAnchor.OUTSIDE_FACE_OF_SUBSTRATE,
  );

  let limitBSP: NodeBSP | null = null;

  const subRoofAABB = aabb2Grow(polyLine2AABB2(boundingPerimeter), -DIST_EPSILON, -DIST_EPSILON);

  let discardEdges: PolyLine2[] = [];
  for (const upperFloorPerimeterId in upperFloorPerimeters) {
    const upperFloorPerimetersForBlock = upperFloorPerimeters[upperFloorPerimeterId];

    for (const upperFloorPerimeter of upperFloorPerimetersForBlock) {
      for (let i = 0, l = polyLine2EdgeCount(upperFloorPerimeter); i < l; i++) {
        const currEdge = polyLine2Edge(upperFloorPerimeter, i);
        const [x0, y0] = polyLine2Start(currEdge);
        const [x1, y1] = polyLine2End(currEdge);

        let discardEdge = !aabb2ContainsVec2(subRoofAABB, x0, y0) && !aabb2ContainsVec2(subRoofAABB, x1, y1);

        if (!discardEdge) {
          continue;
        }

        for (let j = 0, lj = boundingPerimeter.length - 4; j < lj; j += 2) {
          if (line2Side(x0, y0, x1, y1, boundingPerimeter[j], boundingPerimeter[j + 1]) === Line2Side.LEFT) {
            discardEdge = false;
            break;
          }
        }

        if (discardEdge) {
          discardEdges.push(currEdge);
        }
      }
    }
  }

  discardEdges = joinPolyLine2Rails({
    segments: discardEdges,
    tolerance: MULTIGABLE_BSP_EPSILON,
    simplify: true,
    allowRevert: false,
  });

  const maxOverhang = Math.max(...edgeOverhangs);
  for (let i = 0, l = discardEdges.length; i < l; i++) {
    const pl1 = polyLine2Extend(discardEdges[i], exteriorWallOffsetWithSheathing, exteriorWallOffsetWithSheathing);
    const pl2 = polyLine2Reverse(offsetPolyLine2(pl1, -maxOverhang - exteriorWallOffsetWithSheathing));
    const discardPerimeter = polyLine2Concat(pl1, pl2, true);

    const bspPolygons: PolygonBSP[] = [];
    for (let j = 0, lj = polyLine2EdgeCount(discardPerimeter); j < lj; ++j) {
      addEdgeToPolygonList(discardPerimeter, j, 0.0, 10.0, limitBSP, bspPolygons, MULTIGABLE_BSP_EPSILON);
    }

    limitBSP = addPolygonsToBSP(limitBSP, null, bspPolygons, BSP_EPSILON, -1);
  }

  return limitBSP;
}
